import { RefObject, useRef } from 'react'
import {
  DragEndEvent,
  DragStartEvent,
  KeyboardCoordinateGetter,
  KeyboardSensor,
  Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { draggedItem } from './ItemTag'
import { PlacedItem } from './PlannerCell'
import { CarePlanActions } from './carePlanActions'
import { SURVEYS_ROW_KEY } from './carePlanState'
import { TOTAL_WEEKS, TrackRow, WEEKS_PER_MONTH } from './categories'
import { LABEL_COL_SHARE, PlanZoom, WEEK_COL_PX, itemStartWeek, shiftedMonth, shiftedWeek } from './livePlan'

type PlanDragOptions = {
  zoom: PlanZoom
  tableRef: RefObject<HTMLTableElement>
  actions: CarePlanActions
  trackRows: TrackRow[]
  unplannedItemsFor?: (row: TrackRow, planned: PlacedItem[]) => PlacedItem[]
  onMove?: (rowKey: string, item: PlacedItem, toWeek: number, apply: () => void) => void
}

export const usePlanDrag = ({ zoom, tableRef, actions, trackRows, unplannedItemsFor, onMove }: PlanDragOptions) => {
  // One grid unit of the dragged row, in px: a week column, or a month on a monthly row. Set on drag start.
  const stepRef = useRef(0)
  const stepKeys: KeyboardCoordinateGetter = (event, { currentCoordinates }) => {
    const step = stepRef.current || WEEK_COL_PX
    if (event.code === 'ArrowRight') {
      return { ...currentCoordinates, x: currentCoordinates.x + step }
    }
    if (event.code === 'ArrowLeft') {
      return { ...currentCoordinates, x: currentCoordinates.x - step }
    }
    return undefined
  }
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: stepKeys })
  )
  // A bar stays in its own row and snaps to the unit it would land on.
  const snapToRow: Modifier = ({ transform }) => ({
    ...transform,
    y: 0,
    x: stepRef.current ? Math.round(transform.x / stepRef.current) * stepRef.current : transform.x,
  })

  const weekAxis = (rowKey: string) => zoom === 'week' || rowKey === SURVEYS_ROW_KEY

  const onDragStart = ({ active }: DragStartEvent) => {
    const rowKey = draggedItem(active)?.rowKey
    if (!rowKey) {
      return
    }
    const width = tableRef.current?.getBoundingClientRect().width ?? 0
    const column = zoom === 'week' ? WEEK_COL_PX : (width * (1 - LABEL_COL_SHARE)) / TOTAL_WEEKS
    stepRef.current = weekAxis(rowKey) ? column : column * WEEKS_PER_MONTH
  }

  // Moves by how far the bar travelled, not by the cell under the pointer, so a bar can shift by one unit.
  const onDragEnd = ({ active, delta }: DragEndEvent) => {
    const dragged = draggedItem(active)
    if (!dragged) {
      return
    }
    const { rowKey, itemId } = dragged
    const steps = stepRef.current ? Math.round(delta.x / stepRef.current) : 0
    const planned = actions.itemsIn(rowKey)
    const trackRow = trackRows.find((row) => row.key === rowKey)
    const live = trackRow && unplannedItemsFor?.(trackRow, planned).find((item) => item.id === itemId)
    const item = live || planned.find((candidate) => candidate.id === itemId)
    if (!item || steps === 0) {
      return
    }
    const placeAt = (month: number) =>
      live
        ? actions.placeItem(rowKey, month, live.option, live.months, live.trackKey)
        : actions.moveItem(rowKey, itemId, month)

    if (rowKey === SURVEYS_ROW_KEY) {
      placeAt(shiftedWeek(item.startMonth, steps))
      return
    }
    const month = shiftedMonth(item.startMonth, steps)
    const week = weekAxis(rowKey) ? shiftedWeek(itemStartWeek(item), steps) : month * WEEKS_PER_MONTH
    const apply =
      weekAxis(rowKey) && !live
        ? () => actions.scheduleItem(rowKey, itemId, week)
        : () => placeAt(Math.floor(week / WEEKS_PER_MONTH))
    if (onMove) {
      onMove(rowKey, item, week, apply)
    } else {
      apply()
    }
  }

  return { sensors, modifiers: [snapToRow], onDragStart, onDragEnd }
}
